import { useApi } from "@/requests/useApi";
import { UON } from "@/utils";

/**
 * OTS Service - Handles all OTS (Opération Technique Spéciale) API calls
 */

/**
 * Get OTS solutions for a specific VIN
 * Token is automatically cached for 55 minutes by the backend
 *
 * @param vin Vehicle Identification Number (chassis reference)
 * @param params Optional query parameters (colorCode, locale, page, pageSize, sort)
 * @returns OTS solutions response
 */
export const getOtsSolutionsByVin = async (vin: string, params?: any) => {
  try {
    const queryParams = new URLSearchParams();

    // Add orderId for cache management
    if (params?.orderId) queryParams.append('orderId', params.orderId);
    if (params?.colorCode) queryParams.append('colorCode', params.colorCode);
    if (params?.transactionId) queryParams.append('transactionId', params.transactionId);
    if (params?.locale) queryParams.append('locale', params.locale);
    // useCache default true, only add if explicitly false
    if (params?.useCache === false) queryParams.append('useCache', 'false');
    
    const queryString = queryParams.toString();
    const endpoint = `ots/solutions/vin/${vin}${queryString ? '?' + queryString : ''}`;

    const response = await useApi(UON.SystemUid.odmOrchestration, endpoint).get();
    return response.response;
  } catch (error) {
    console.error('Error fetching OTS solutions:', error);
    throw error;
  }
};

/**
 * Check if a VIN has any OTS (quick check without full details)
 *
 * @param vin Vehicle Identification Number
 * @returns Boolean indicating if OTS exist
 */
export const checkOtsExist = async (vin: string): Promise<boolean> => {
  try {
    const response = await getOtsSolutionsByVin(vin, {
      pageSize: 1,
      locale: 'fr_FR'
    });
    return response && response.totalElements > 0;
  } catch (error) {
    console.error('Error checking OTS existence:', error);
    return false;
  }
};

/**
 * Get OTS color severity mapping for UI display
 */
export const getOtsColorClass = (colorCode: string): string => {
  const colorMap: { [key: string]: string } = {
    'R': 'ots-critical',    
    'O': 'ots-important',   
    'J': 'ots-warning',     
    'B': 'ots-info',       
    'V': 'ots-normal',      
    'I': 'ots-general',  
    'S': 'ots-special'    
  };
  return colorMap[colorCode] || 'ots-general';
};

/**
 * Get OTS severity icon for UI display
 */
export const getOtsSeverityIcon = (colorCode: string): string => {
  const iconMap: { [key: string]: string } = {
    'R': '⚠️', 
    'O': '🔶',  
    'J': '⚡',  
    'B': 'ℹ️',  
    'V': '✅',  
    'I': '📋',  
    'S': '⭐' 
  };
  return iconMap[colorCode] || 'ℹ️';
};

/**
 * Get OTS color label in French
 */
export const getOtsColorLabel = (colorCode: string): string => {
  const labelMap: { [key: string]: string } = {
    'R': 'Sécurité',
    'O': 'Important',
    'J': 'Avertissement',
    'B': 'Information',
    'V': 'Normal',
    'I': 'Info',
    'S': 'Spécial'
  };
  return labelMap[colorCode] || 'Information';
};
